import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { DescriptionOutlined as DescriptionOutlinedIcon } from 'components/icons';
import type { CellContext } from '@tanstack/react-table';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { ContractListItem, ContractStatusType, ContractType } from 'types/contracts';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { useClassifiers } from 'utils/useClassifiers';

import StatusChip from 'components/@extended/StatusChip';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import Loader from 'components/Loader';
import MatterIndicators from 'components/matters/MatterIndicators';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap.tsx';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Tooltip } from 'components/ui/tooltip';
import { getFilterOptionsFromClassifier, mapToFilterOptions } from 'components/tables/list-table/filterUtil.ts';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { PricelistDetailsFetchingCard } from 'pages/pricelists/PricelistDetailsFetchingCard';
import { useLazyGetCompaniesWithFiltersQuery } from 'store/api/companies';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetContractsCountQuery, useLazyGetContractsWithFiltersQuery } from 'store/api/contracts';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinGroupsListWithFiltersQuery } from 'store/api/groups';
import { useLazyGetMattersWithFiltersQuery } from 'store/api/matters';
import { CustomFieldValue } from 'types/customFields';
import { EntityType } from 'types/entity';
import { FeatureFlag } from 'types/features';
import { ConnectedMatterDto } from 'types/pricelists';
import { useCustomFieldsList } from 'utils/useCustomFieldsList';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { cn } from 'utils/cn';
import { ENTITY_LINK_CLASS } from 'pages/matters/util';
import { contractStatusLabel, getAppliedInOffice, getContractParties, getContractStatusChipType } from './util';

const filterStatusTypes = [ContractStatusType.VALID, ContractStatusType.TERMINATED, ContractStatusType.DRAFT];

/** The list's count, with the drafts it lists. */
const useLazyContractsCountWithDraftsQuery: typeof useLazyGetContractsCountQuery = (options) => {
    const [count, ...rest] = useLazyGetContractsCountQuery(options);
    return [useWithDrafts(count), ...rest];
};

const getContractHref = (contract: ContractListItem) => `/contract/${contract.id}`;

const ContractsTable = ({
    passiveFilter = '',
    disableUrlState = false,
    extraHeaderComponent,
}: {
    passiveFilter?: string;
    disableUrlState?: boolean;
    extraHeaderComponent?: React.ReactNode;
}): JSX.Element => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isNewDesign = useIsNewDesign();
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: [
            'CONTRACT_STATUS_TYPE',
            'CONTRACT_TYPE',
            'CONTRACT_RELATED_KEYWORD_TYPE',
            'CURRENCY',
            'MATTER_STATE_TYPE',
        ],
    });

    const { customFieldsArray, isLoading: isLoadingCustomFieldsList } = useCustomFieldsList(EntityType.CONTRACT);
    const isDeadlineCustomFieldShownInTable = useIsFeatureEnabled(
        FeatureFlag.SHOW_DEADLINE_CUSTOM_FIELD_CONTRACT_TABLE
    );

    const [fetchContracts, { data, isFetching }] = useLazyGetContractsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchContracts);

    const columns = useMemo<DataTableColumnDef<ContractListItem>[]>(
        () => [
            {
                header: t('contracts.name-concluded-by'),
                accessorKey: 'contractName',
                enableGrouping: false,
                // Not sortable; the signed-on date has its own sortable column.
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ cell }) => {
                    const { id, contractName, tenantCompany, isDraft } = cell.row.original;
                    return (
                        <div className="flex flex-col">
                            <div className="flex items-start justify-between gap-1">
                                <RouterLink className={cn(ENTITY_LINK_CLASS, 'font-semibold')} to={`/contract/${id}`}>
                                    {contractName ?? '-'}
                                </RouterLink>
                                {/* The list has no actions column, so a draft's menu sits by its name. */}
                                {isDraft ? <DraftRowMenu draft={{ type: 'contract', id }} className="size-7" /> : null}
                            </div>
                            {tenantCompany?.name ?? '-'}
                        </div>
                    );
                },
            },
            {
                header: t('contracts.status'),
                id: 'contractStatusTypeCode',
                accessorKey: 'contractStatusTypeCode',
                enableGrouping: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ cell }) => {
                    const { dmsLink, contractStatusTypeCode, isDraft } = cell.row.original;
                    return (
                        <div className="flex flex-col flex-nowrap items-start gap-1">
                            {isDraft ? <DraftChip /> : null}
                            <StatusChip
                                type={getContractStatusChipType(contractStatusTypeCode)}
                                label={contractStatusLabel(cell.row.original, classifiers)}
                            />
                            {dmsLink && !isDraft && <StatusChip link={dmsLink} type="link" label="DMS" />}
                        </div>
                    );
                },
            },
            {
                header: t('contracts.applied-in-offices'),
                accessorKey: 'tenantCompany',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    // The one column measuring beyond its peers (~1.41x, stable at every width) — the
                    // office list is the widest content in the row.
                    widthWeight: 1.41,
                    minPx: 180,
                },
                cell: ({ getValue, cell }) => {
                    return getValue() ? <div>{getAppliedInOffice(cell.row.original)}</div> : null;
                },
            },
            {
                header: t('contracts.connected-contacts'),
                accessorKey: 'parties',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value ? <div>{getContractParties(cloneDeep(value))}</div> : null;
                },
            },
            {
                header: t('contracts.connected-cases'),
                accessorKey: 'matters',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    // Wide enough for the new design's "Confidential case" chip under a case.
                    minPx: isNewDesign ? 150 : 130,
                },
                cell: ({ getValue }) => (
                    <div className="flex flex-col justify-start whitespace-normal">
                        <ConnectedCases matters={cloneDeep(getValue() ?? [])} />
                    </div>
                ),
            },
            {
                header: t('contracts.signed-on-date'),
                id: 'contractSignedAt',
                accessorKey: 'contractSignedAt',
                enableGrouping: false,
                meta: {
                    widthWeight: 1,
                    minPx: 130,
                },
                cell: ({ getValue }) => {
                    const value = getValue();
                    return value && <DateDefaultNowrap dateValue={value} />;
                },
            },
            ...(isDeadlineCustomFieldShownInTable
                ? [
                      {
                          header: t('custom-fields.deadline'),
                          // Explicit id: without one the managed layout would key this column on
                          // its translated Header, so a language switch would orphan its
                          // persisted width / visibility.
                          id: 'deadlineCustomField',
                          enableGrouping: false,
                          // Not sortable: the value is a per-row custom-field lookup, not a contract column.
                          enableSorting: false,
                          meta: {
                              widthWeight: 1,
                              minPx: 130,
                          },
                          cell: ({ cell }: CellContext<ContractListItem, unknown>) => {
                              const customFieldValues = (cell.row.original?.customFieldValues ||
                                  []) as CustomFieldValue[];

                              const customField = customFieldsArray.find(
                                  (cfa) =>
                                      cfa.entityType === EntityType.CONTRACT &&
                                      cfa.label === 'Deadline' &&
                                      cfa.fieldType === 'DATE'
                              );
                              const customFieldValue = customFieldValues.find(
                                  (cfv) => cfv.customFieldId == customField?.id
                              )?.value;
                              // customFieldValue is `unknown` from CustomFieldValue.value; only render when it's
                              // a FormattedDate-compatible primitive. fieldType === 'DATE' is asserted above but
                              // the value's runtime type still needs the narrow.
                              const dateValue =
                                  typeof customFieldValue === 'string' ||
                                  typeof customFieldValue === 'number' ||
                                  customFieldValue instanceof Date
                                      ? customFieldValue
                                      : undefined;

                              return (
                                  <p className="text-body1">
                                      <FormattedDate value={dateValue} />
                                  </p>
                              );
                          },
                      },
                  ]
                : []),
            {
                header: t('contracts.pricelist'),
                id: 'pricelist',
                enableGrouping: false,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    // Room for a range with its two icons; a narrower cell ellipsises the range.
                    minPx: 190,
                },
                cell: ({ row }) => {
                    const { contractTypeCode, pricelist, isDraft } = row.original;
                    // A draft's pricing stays its own: nothing reads its rates until it is created.
                    if (isDraft) return '—';
                    const isAmlOrUbo = contractTypeCode === ContractType.AML || contractTypeCode === ContractType.UBO;
                    const isRepresentation = contractTypeCode === ContractType.REPRESENTATION;
                    return !isAmlOrUbo && !isRepresentation && pricelist ? (
                        <PricelistDetailsFetchingCard pricelist={pricelist} />
                    ) : null;
                },
            },
        ],
        [classifiers, customFieldsArray, isDeadlineCustomFieldShownInTable, isNewDesign]
    );

    const contractStatusTypeOptions = useMemo(
        () => mapToFilterOptions(filterStatusTypes, classifiers['CONTRACT_STATUS_TYPE']),
        [classifiers]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'contractStatusTypeCode',
                    chipLabel: 'filters.status',
                    filters: contractStatusTypeOptions,
                },
            ],
            filters: [
                // General
                {
                    type: 'multiselect',
                    label: t('contracts.filter.contract-type'),
                    selector: 'contractTypeCode',
                    group: 'contracts.filter.general',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CONTRACT_TYPE']) : [],
                },
                {
                    type: 'text',
                    label: t('contracts.filter.contract-number'),
                    selector: 'contractNumber',
                    group: 'contracts.filter.general',
                    tooltip: 'contracts.tooltips.contract-number-filter',
                },
                {
                    type: 'multiselect',
                    label: t('contracts.filter.contract-keywords'),
                    selector: 'contractRelatedKeywords.contractRelatedKeywordCode',
                    group: 'contracts.filter.general',
                    options: classifiers
                        ? getFilterOptionsFromClassifier(classifiers?.['CONTRACT_RELATED_KEYWORD_TYPE'])
                        : [],
                },
                {
                    type: 'checkboxgroup',
                    label: t('contracts.filter.client-template'),
                    selector: 'isClientTemplate',
                    group: 'contracts.filter.general',
                    tooltip: 'contracts.tooltips.client-template',
                    filters: [
                        { label: t('yes'), argument: 'true' },
                        { label: t('no'), argument: 'false' },
                    ],
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('contracts.filter.concluded-by'),
                    selector: 'tenantCompany.id',
                    group: 'contracts.filter.general',
                    paginated: {
                        fetch: useLazyGetCompaniesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('contracts.filter.applied-in'),
                    selector: 'companies.company.id',
                    group: 'contracts.filter.general',
                    paginated: {
                        fetch: useLazyGetCompaniesWithFiltersQuery,
                        searchField: 'name',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                // Financial Conditions & Billing
                {
                    type: 'multiselect',
                    label: t('contracts.filter.currency'),
                    selector: 'financialCondition.currencyTypeCode',
                    group: 'contracts.filter.financial',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['CURRENCY']) : [],
                },
                {
                    type: 'checkboxgroup',
                    label: t('contracts.filter.uses-model-price-list'),
                    selector: 'id',
                    comperator: '=usesmodelpricelist=',
                    group: 'contracts.filter.financial',
                    tooltip: 'contracts.tooltips.uses-model-price-list',
                    filters: [
                        { label: t('yes'), argument: 'true' },
                        { label: t('no'), argument: 'false' },
                    ],
                },
                {
                    type: 'checkboxgroup',
                    label: t('contracts.filter.has-discount'),
                    selector: 'id',
                    comperator: '=hasdiscount=',
                    group: 'contracts.filter.financial',
                    tooltip: 'contracts.tooltips.has-discount',
                    filters: [
                        { label: t('yes'), argument: 'true' },
                        { label: t('no'), argument: 'false' },
                    ],
                },
                {
                    type: 'interval',
                    label: t('contracts.filter.discount-pct'),
                    selector: 'id',
                    comperator: '=discountpctrange=',
                    group: 'contracts.filter.financial',
                    minLabel: t('contracts.filter.discount-pct-from'),
                    maxLabel: t('contracts.filter.discount-pct-to'),
                },
                {
                    type: 'checkboxgroup',
                    label: t('contracts.filter.has-active-price-list'),
                    selector: 'id',
                    comperator: '=hasactivepricelist=',
                    group: 'contracts.filter.financial',
                    tooltip: 'contracts.tooltips.has-active-price-list',
                    filters: [
                        { label: t('yes'), argument: 'true' },
                        { label: t('no'), argument: 'false' },
                    ],
                },
                // Status and Dates
                {
                    type: 'multiselect',
                    label: t('contracts.status'),
                    selector: 'contractStatusTypeCode',
                    group: 'contracts.filter.status-dates',
                    canExclude: true,
                    options: contractStatusTypeOptions,
                },
                {
                    type: 'checkboxgroup',
                    label: t('contracts.filter.sent-but-not-signed'),
                    selector: 'id',
                    comperator: '=sentnotsigned=',
                    group: 'contracts.filter.status-dates',
                    tooltip: 'contracts.tooltips.sent-but-not-signed',
                    filters: [{ label: t('yes'), argument: 'true' }],
                },
                {
                    type: 'offset-date-time-range',
                    label: t('contracts.filter.sent-on'),
                    selector: 'contractSentAt',
                    comperator: '=daterange=',
                    group: 'contracts.filter.status-dates',
                    shouldDisplayQuickPick: false,
                },
                {
                    type: 'offset-date-time-range',
                    label: t('contracts.filter.signed-on'),
                    selector: 'contractSignedAt',
                    comperator: '=daterange=',
                    group: 'contracts.filter.status-dates',
                    shouldDisplayQuickPick: false,
                },
                {
                    type: 'offset-date-time-range',
                    label: t('contracts.filter.terminated-on'),
                    selector: 'contractTerminatedAt',
                    comperator: '=daterange=',
                    group: 'contracts.filter.status-dates',
                    shouldDisplayQuickPick: false,
                },
                // Client
                {
                    type: 'dynamicmultiselect',
                    label: t('contracts.filter.connected-contacts'),
                    selector: 'parties.party.id',
                    group: 'contracts.filter.client',
                    paginated: {
                        fetch: useLazyGetMinContactsListWithFiltersQuery,
                        searchField: ['name', 'partyNumber'],
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('contracts.filter.client-group'),
                    selector: 'id',
                    comperator: '=contracthaspartyingroup=',
                    group: 'contracts.filter.client',
                    tooltip: 'contracts.tooltips.client-group',
                    paginated: {
                        fetch: useLazyGetMinGroupsListWithFiltersQuery,
                        searchField: 'groupName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'groupName',
                    },
                },
                {
                    type: 'dynamicmultiselect',
                    label: t('contracts.filter.relationship-partner'),
                    selector: 'id',
                    comperator: '=contracthaspartypartner=',
                    group: 'contracts.filter.client',
                    tooltip: 'contracts.tooltips.relationship-partner',
                    paginated: {
                        fetch: useLazyGetEmployeesListWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'name',
                    },
                },
                {
                    type: 'offset-date-time-range',
                    label: t('contracts.filter.last-timecard'),
                    labelTo: t('contracts.filter.last-timecard-to'),
                    selector: 'id',
                    comperator: '=contractlasttimecard=',
                    group: 'contracts.filter.client',
                    tooltip: 'contracts.tooltips.last-timecard',
                    shouldDisplayQuickPick: false,
                },
                // Case
                {
                    type: 'dynamicmultiselect',
                    label: t('contracts.filter.connected-case'),
                    selector: 'matters.id',
                    group: 'contracts.filter.case',
                    tooltip: 'contracts.tooltips.connected-case',
                    getOptionStartAdornment: renderMatterIndicators,
                    paginated: {
                        fetch: useLazyGetMattersWithFiltersQuery,
                        searchField: 'fullName',
                        getOptionIdentifier: 'id',
                        getOptionLabel: 'fullName',
                    },
                },
                {
                    type: 'multiselect',
                    label: t('contracts.filter.case-status'),
                    selector: 'matters.matterStateTypeCode',
                    group: 'contracts.filter.case',
                    tooltip: 'contracts.tooltips.case-status',
                    options: classifiers ? getFilterOptionsFromClassifier(classifiers?.['MATTER_STATE_TYPE']) : [],
                },
                {
                    type: 'daterange',
                    label: t('contracts.filter.case-opening-date'),
                    labelTo: t('contracts.filter.case-opening-date-to'),
                    selector: 'matters.openedDate',
                    group: 'contracts.filter.case',
                    tooltip: 'contracts.tooltips.case-opening-date',
                    shouldDisplayQuickPick: false,
                },
            ],
            searchBarSelectors: [
                { selector: 'contractName' },
                { selector: 'companies.company.name' },
                { selector: 'tenantCompany.name' },
                { selector: 'parties.party.name' },
                { selector: 'tenantCompany.offices.name' },
            ],
            passiveFilter: passiveFilter,
        }),
        [contractStatusTypeOptions, classifiers, passiveFilter]
    );

    if (isLoadingClassifiers || isLoadingCustomFieldsList) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ContractListItem>
            getRowHref={getContractHref}
            listKey="contracts"
            columnConfigId="contracts"
            columns={columns}
            data={data}
            isLoading={isFetching}
            pluralTextId="contracts.contracts"
            singularTextId="contract"
            noEntriesId="contracts.no-contracts-found"
            noEntriesBodyId="contracts.empty.body"
            noEntriesIcon={DescriptionOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.CONTRACTS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/contract/new')}
                    labelId="contracts.add-new-contract"
                    newLabelId="new-entity.contract"
                />
            }
            fetchData={fetchData}
            fetchCount={useLazyContractsCountWithDraftsQuery}
            filterConfig={filterConfig}
            rowsPerPage={50}
            disableUrlState={disableUrlState}
            extraHeaderComponent={extraHeaderComponent}
            canRememberFilters={true}
        />
    );
};

const ConnectedCases: React.FC<{ matters: ConnectedMatterDto[] }> = React.memo(
    ({ matters }) => {
        const isNewDesign = useIsNewDesign();
        const [isExpanded, setIsExpanded] = useState(false);
        const firstHalf: React.ReactNode[] = [];
        const secondHalf: React.ReactNode[] = [];

        (matters || []).forEach((item, index) => {
            const optionalComma = index < (matters || []).length - 1 ? `,\u00A0` : null;
            const fullName = `${item.matterNumber} ${item.matterName}`;

            (index < 2 ? firstHalf : secondHalf).push(
                isNewDesign ? (
                    // One case a line, its indicators on a line of their own under it.
                    <div key={item.matterId} className="flex flex-col items-start gap-1">
                        <RecordLink className={ENTITY_LINK_CLASS} to={`/matter/${item.matterId}`} label={fullName} />
                        <MatterIndicators isConfidential={item.isConfidential} />
                    </div>
                ) : (
                    <React.Fragment key={item.matterId}>
                        <MatterIndicators isConfidential={item.isConfidential} className="mr-1" />
                        <RouterLink className={ENTITY_LINK_CLASS} to={`/matter/${item.matterId}`}>
                            {fullName}
                        </RouterLink>
                        {optionalComma}
                    </React.Fragment>
                )
            );
        });

        return (
            <div>
                {firstHalf.concat(
                    isExpanded
                        ? secondHalf
                        : [
                              secondHalf.length > 0 ? (
                                  <Tooltip
                                      key={'connected-cases-tooltip'}
                                      content={secondHalf}
                                      className="w-fit text-center"
                                  >
                                      <button
                                          type="button"
                                          className="text-link text-body1 cursor-pointer font-semibold"
                                          onClick={() => setIsExpanded(!isExpanded)}
                                      >{`+${secondHalf.length}`}</button>
                                  </Tooltip>
                              ) : null,
                          ]
                )}
            </div>
        );
    },
    (prevProps, currentProps) => isEqual(prevProps.matters, currentProps.matters)
);

export default ContractsTable;
